/**
 * Organisation vocabulary shared by the add-organisation wizard, the edit
 * dialog and (eventually) onboarding — one place for the types a user can
 * add, the placements a council or agency can have underneath them, and
 * the roles people held at each kind of place.
 */
import {
  Building2,
  Heart,
  Handshake,
  Home,
  Users,
  GraduationCap,
  Stethoscope,
  Building,
  ShieldCheck,
  Brain,
} from 'lucide-react';

export const ORG_TYPES = [
  { value: 'council', label: 'Local Council', singular: 'council', icon: Building2, description: 'The council responsible for your care', prompt: 'Do you remember which council was responsible for you?' },
  { value: 'fostering_agency', label: 'Fostering Agency', singular: 'fostering agency', icon: Handshake, description: 'If an agency placed you with foster carers', prompt: 'Was a fostering agency ever involved, alongside the council?' },
  { value: 'childrens_home', label: "Children's Home", singular: "children's home", icon: Home, description: 'A residential home you lived in', prompt: 'Did you live in a children’s home at any point?' },
  { value: 'adoption', label: 'Adoption Agency', singular: 'adoption agency', icon: Users, description: 'If adoption was involved', prompt: 'Was adoption ever part of your story?' },
  { value: 'school', label: 'School', singular: 'school', icon: GraduationCap, description: 'Primary, secondary or college', prompt: 'Do you remember your secondary school, or any other school you went to?' },
  { value: 'doctor', label: 'GP Surgery', singular: 'GP surgery', icon: Stethoscope, description: 'A doctor you were registered with', prompt: 'Were you registered with a GP surgery?' },
  { value: 'hospital', label: 'Hospital', singular: 'hospital', icon: Building, description: 'A visit or a stay', prompt: 'Any hospital visits or stays during your time in care?' },
  { value: 'police', label: 'Police', singular: 'police force', icon: ShieldCheck, description: 'Any police involvement', prompt: 'Was the police ever involved at any point?' },
  { value: 'mental_health', label: 'Mental Health Service', singular: 'mental health service', icon: Brain, description: 'CAMHS or other services', prompt: 'Did you ever see a mental health service, like CAMHS?' },
];

/**
 * Light-touch "why not add another organisation" nudges shown on the
 * dashboard once someone has at least one organisation — never at
 * onboarding, which only asks for the first one. Order matters: the types
 * people most often forget (schools, GPs, hospitals) come before the rarer
 * ones. `council` is deliberately last — it's almost always the first
 * organisation someone adds, so suggesting it again is rarely useful.
 */
export const NEXT_ORG_SUGGESTION_ORDER = [
  'school', 'doctor', 'hospital', 'mental_health', 'police',
  'childrens_home', 'fostering_agency', 'adoption', 'council',
];

/**
 * The placement-first door in the add wizard. Not an organisation type —
 * nobody holds a file at a foster home — it creates a placement under the
 * council or agency that placed the user there, or under a "Council not yet
 * known" holder when they don't know yet (backend services/placements.py).
 */
export const FOSTER_HOME_TILE = {
  value: 'foster_home',
  label: 'Foster home',
  singular: 'foster home',
  icon: Heart,
  description: 'Where you lived — the carers, not the council',
  virtual: true,
};

export const WIZARD_TILES = [FOSTER_HOME_TILE, ...ORG_TYPES];

export const getOrgType = (value) =>
  (value === FOSTER_HOME_TILE.value ? FOSTER_HOME_TILE : ORG_TYPES.find((t) => t.value === value)) || null;

// Organisation types that place people in homes (and so can hold placements).
export const PLACING_ORG_TYPES = ['council', 'fostering_agency'];
// Organisation types that are themselves a home someone was placed in, so
// the wizard asks "who placed you there?" when adding one.
export const PLACED_HOME_ORG_TYPES = ['childrens_home'];
export const HOLDING_ORG_NAME = 'Council not yet known';

// Only these org types can have placements underneath them.
export const ORG_TYPES_WITH_PLACEMENTS = ['council', 'fostering_agency'];

export const PLACEMENT_TYPES = [
  { value: 'foster_placement', label: 'Foster Placement' },
  { value: 'childrens_home', label: "Children's Home" },
  { value: 'residential', label: 'Residential Unit' },
  { value: 'kinship', label: 'Kinship Care' },
  { value: 'supported_lodgings', label: 'Supported Lodgings' },
  { value: 'semi_independent', label: 'Semi-Independent Living' },
];

// Carer-led placements are in someone's home; building-led placements are
// run from a property, usually by a third party with its own records.
export const CARER_LED_TYPES = new Set(['foster_placement', 'kinship', 'supported_lodgings']);
export const BUILDING_LED_TYPES = new Set(['childrens_home', 'residential', 'semi_independent']);

export const PROFESSIONAL_ROLES = {
  council: [
    { value: 'social_worker', label: 'Social Worker' },
    { value: 'iro', label: 'Independent Reviewing Officer (IRO)' },
    { value: 'team_manager', label: 'Team Manager' },
    { value: 'family_support', label: 'Family Support Worker' },
    { value: 'leaving_care_pa', label: 'Leaving Care Personal Advisor' },
    { value: 'foster_carer', label: 'Foster Carer' },
    { value: 'supervising_sw', label: 'Supervising Social Worker' },
  ],
  fostering_agency: [
    { value: 'supervising_sw', label: 'Supervising Social Worker' },
    { value: 'foster_carer', label: 'Foster Carer' },
    { value: 'support_worker', label: 'Support Worker' },
    { value: 'recruitment_officer', label: 'Recruitment Officer' },
  ],
  childrens_home: [
    { value: 'key_worker', label: 'Key Worker' },
    { value: 'residential_worker', label: 'Residential Worker' },
    { value: 'home_manager', label: 'Home Manager' },
    { value: 'deputy_manager', label: 'Deputy Manager' },
  ],
  adoption: [
    { value: 'adoption_sw', label: 'Adoption Social Worker' },
    { value: 'family_finder', label: 'Family Finder' },
  ],
  school: [
    { value: 'teacher', label: 'Teacher' },
    { value: 'form_tutor', label: 'Form Tutor' },
    { value: 'head_of_year', label: 'Head of Year' },
    { value: 'headteacher', label: 'Headteacher' },
    { value: 'senco', label: 'SENCO' },
    { value: 'designated_teacher', label: 'Designated Teacher for LAC' },
  ],
  doctor: [
    { value: 'gp', label: 'GP' },
    { value: 'practice_nurse', label: 'Practice Nurse' },
    { value: 'health_visitor', label: 'Health Visitor' },
  ],
  hospital: [
    { value: 'consultant', label: 'Consultant' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'nurse', label: 'Nurse' },
    { value: 'paediatrician', label: 'Paediatrician' },
  ],
  police: [
    { value: 'police_officer', label: 'Police Officer' },
    { value: 'detective', label: 'Detective' },
    { value: 'youth_liaison', label: 'Youth Liaison Officer' },
  ],
  mental_health: [
    { value: 'psychiatrist', label: 'Psychiatrist' },
    { value: 'psychologist', label: 'Psychologist' },
    { value: 'therapist', label: 'Therapist' },
    { value: 'camhs_worker', label: 'CAMHS Worker' },
  ],
};

export const rolesFor = (type) => PROFESSIONAL_ROLES[type] || PROFESSIONAL_ROLES.council;

export const roleLabel = (type, value) =>
  rolesFor(type).find((r) => r.value === value)?.label
  || Object.values(PROFESSIONAL_ROLES).flat().find((r) => r.value === value)?.label
  || value;

/** "Sep 2009" from "2009-09"; "" for blank. */
export const formatMonthYear = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = parseInt(m, 10) - 1;
  return names[mi] ? `${names[mi]} ${y}` : y;
};

// Short breaks (respite). Mirrors backend services/placements.RESPITE_PATTERNS.
export const RESPITE_PATTERNS = [
  { value: 'one_off', label: 'Once' },
  { value: 'a_few_times', label: 'A few times' },
  { value: 'regular_weekends', label: 'Regular weekends' },
  { value: 'school_holidays', label: 'School holidays' },
  { value: 'carers_away', label: 'While the carers were away' },
  { value: 'emergency', label: 'In an emergency' },
];
export const respitePatternLabel = (value) => RESPITE_PATTERNS.find((p) => p.value === value)?.label || null;
